import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ColorEditorPopover } from '../autoplay/ColorEditorPopover';
import { ThumbnailOverlay } from '@/store/videoFeatures';
import { getButtonProperty } from './ThumbnailButtonUtils';
import { Wand2 } from 'lucide-react';
import { ensureHttps } from '@/utils/url';

interface ThumbnailButtonConfigProps {
  thumbnail: ThumbnailOverlay;
  onButtonPropertyChange: (property: string, value: any) => void;
}

export const ThumbnailButtonConfig: React.FC<ThumbnailButtonConfigProps> = ({
  thumbnail,
  onButtonPropertyChange,
}) => {
  useLanguage();
  if (!thumbnail.actionButton) return null;
  
  return (
    <div className="space-y-5">
      {/* Grid 2 Colunas - Inputs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
        {/* Coluna A - Campo 1 */}
        <div className="grid gap-2">
          <Label htmlFor="button-text" className="text-sm font-medium">{translateInterfaceText("Texto do Botão")}</Label>
          <Input
            id="button-text"
            value={getButtonProperty(thumbnail, 'text')}
            onChange={(e) => onButtonPropertyChange('text', e.target.value)}
            placeholder={translateInterfaceText("Continue Assistindo")}
          />
        </div>
        
        {/* Coluna B - Campo 1 */}
        <div className="grid gap-2">
          <Label className="text-sm font-medium">{translateInterfaceText("Tamanho")}</Label>
          <Select 
            value={getButtonProperty(thumbnail, 'size') || 'medio'}
            onValueChange={(value) => onButtonPropertyChange('size', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={translateInterfaceText("Selecione o tamanho")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pequeno">{translateInterfaceText("Pequeno")}</SelectItem>
              <SelectItem value="medio">{translateInterfaceText("Médio")}</SelectItem>
              <SelectItem value="grande">{translateInterfaceText("Grande")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Coluna A - Campo 2 */}
        <div className="grid gap-2">
          <Label htmlFor="button-link" className="text-sm font-medium">{translateInterfaceText("Link do Botão")}</Label>
          <Input
            id="button-link"
            value={getButtonProperty(thumbnail, 'link') || getButtonProperty(thumbnail, 'url')}
            onChange={(e) => onButtonPropertyChange('link', e.target.value)}
            onBlur={(e) => {
              const urlWithProtocol = ensureHttps(e.target.value);
              if (urlWithProtocol !== e.target.value) {
                onButtonPropertyChange('link', urlWithProtocol);
              }
            }}
            placeholder="https://exemplo.com.br"
          />
        </div>
        
        {/* Coluna B - Campo 2 */}
        <div className="grid gap-2">
          <Label className="text-sm font-medium">{translateInterfaceText("Posição")}</Label>
          <Select
            value={getButtonProperty(thumbnail, 'position') || 'bottom-center'}
            onValueChange={(value) => onButtonPropertyChange('position', value as any)}
          >
            <SelectTrigger>
              <SelectValue placeholder={translateInterfaceText("Selecione a posição")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="top-left">{translateInterfaceText("Superior Esquerda")}</SelectItem>
              <SelectItem value="top-center">{translateInterfaceText("Superior Centro")}</SelectItem>
              <SelectItem value="top-right">{translateInterfaceText("Superior Direita")}</SelectItem>
              <SelectItem value="center-left">{translateInterfaceText("Centro Esquerda")}</SelectItem>
              <SelectItem value="center-right">{translateInterfaceText("Centro Direita")}</SelectItem>
              <SelectItem value="bottom-left">{translateInterfaceText("Inferior Esquerda")}</SelectItem>
              <SelectItem value="bottom-center">{translateInterfaceText("Inferior Centro")}</SelectItem>
              <SelectItem value="bottom-right">{translateInterfaceText("Inferior Direita")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
      </div>
      
      {/* Seção de Cores - 4 em linha única */}
      <div className="space-y-3 pt-2">
        <h3 className="text-[14px] font-medium text-[#475569]">{translateInterfaceText("Cores do Botão")}</h3>
        
        <div className="flex items-center justify-between gap-3">
          {/* Fundo */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] font-medium text-[#5A5F66]">{translateInterfaceText("Fundo")}</span>
            <ColorEditorPopover
              colorName={translateInterfaceText("Fundo")}
              currentColor={getButtonProperty(thumbnail, 'colors.background') || '#3b82f6'}
              onSave={(color) => onButtonPropertyChange('colors.background', color)}
            >
              <button type="button" className="w-9 h-9 rounded-full border border-border transition-all duration-200 hover:shadow-[0_0_0_3px_rgba(77,111,255,0.25)] cursor-pointer" style={{ backgroundColor: getButtonProperty(thumbnail, 'colors.background') || '#3b82f6' }} />
            </ColorEditorPopover>
          </div>
          
          {/* Texto */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] font-medium text-[#5A5F66]">{translateInterfaceText("Texto")}</span>
            <ColorEditorPopover
              colorName={translateInterfaceText("Texto")}
              currentColor={getButtonProperty(thumbnail, 'colors.text') || '#ffffff'}
              onSave={(color) => onButtonPropertyChange('colors.text', color)}
            >
              <button type="button" className="w-9 h-9 rounded-full border border-border transition-all duration-200 hover:shadow-[0_0_0_3px_rgba(77,111,255,0.25)] cursor-pointer" style={{ backgroundColor: getButtonProperty(thumbnail, 'colors.text') || '#ffffff' }} />
            </ColorEditorPopover>
          </div>
          
          {/* Hover Fundo */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] font-medium text-[#5A5F66]">{translateInterfaceText("Hover Fundo")}</span>
            <ColorEditorPopover
              colorName={translateInterfaceText("Hover Fundo")}
              currentColor={getButtonProperty(thumbnail, 'colors.hover') || '#2563eb'}
              onSave={(color) => onButtonPropertyChange('colors.hover', color)}
            >
              <button type="button" className="w-9 h-9 rounded-full border border-border transition-all duration-200 hover:shadow-[0_0_0_3px_rgba(77,111,255,0.25)] cursor-pointer" style={{ backgroundColor: getButtonProperty(thumbnail, 'colors.hover') || '#2563eb' }} />
            </ColorEditorPopover>
          </div>
          
          {/* Hover Texto */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] font-medium text-[#5A5F66]">{translateInterfaceText("Hover Texto")}</span>
            <ColorEditorPopover
              colorName={translateInterfaceText("Hover Texto")}
              currentColor={getButtonProperty(thumbnail, 'colors.hoverText') || '#ffffff'}
              onSave={(color) => onButtonPropertyChange('colors.hoverText', color)}
            >
              <button type="button" className="w-9 h-9 rounded-full border border-border transition-all duration-200 hover:shadow-[0_0_0_3px_rgba(77,111,255,0.25)] cursor-pointer" style={{ backgroundColor: getButtonProperty(thumbnail, 'colors.hoverText') || '#ffffff' }} />
            </ColorEditorPopover>
          </div>
        </div>
      </div>
      
    </div>
  );
};
